import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import ImageViewer from "./ImageViewer";
import { LevelBadge, MannerBadge, SportLevelBadge } from "./Badges";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";
import pickleballIcon from "./pickleball.png";
import MatchResult from "./MatchResult";
import MannerReview from "./MannerReview";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("ko-KR", {
    year: "numeric", month: "long", day: "numeric", weekday: "short",
  });
};

export default function ChatRoom({ room, myTeamName, onBack, onMarkRead }) {
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState("");
  const [loading, setLoading]         = useState(true);
  const [sending, setSending]         = useState(false);
  const [schedule, setSchedule]       = useState(null);
  const [showSchedule, setShowSchedule]   = useState(false);
  const [schedForm, setSchedForm]     = useState({ game_date: "", game_time: "", location: "", memo: "" });
  const [schedLoading, setSchedLoading]   = useState(false);
  const [opponentTeam, setOpponentTeam]       = useState(null);
  const [showTeamModal, setShowTeamModal]     = useState(false);
  const [isMatchConfirmed, setIsMatchConfirmed]   = useState(false);
  const [matchStatusLoaded, setMatchStatusLoaded] = useState(false);
  const [isHost, setIsHost]                       = useState(false);
  const [showConfirmModal, setShowConfirmModal]   = useState(false);
  const [confirmLoading, setConfirmLoading]       = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm]   = useState(false);
  const [leaving, setLeaving]                     = useState(false);
  const [appData,          setAppData]            = useState(null);
  const [myTeam,           setMyTeam]             = useState(null);
  const [matchResult,      setMatchResult]        = useState(null);
  const [showScore,        setShowScore]          = useState(false);
  const [scoreRefreshKey,  setScoreRefreshKey]    = useState(0);
  const [showManner,       setShowManner]         = useState(false);
  const [hasReviewed,      setHasReviewed]        = useState(false);
  const [imageUploading,   setImageUploading]     = useState(false);
  const [chatImageViewer,  setChatImageViewer]    = useState(null);

  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const opponentTeamIdRef = useRef(null);
  const postDataRef = useRef(null); // 모집글 일정 자동완성용
  const postIdRef   = useRef(null); // 최종 승인 시 post 마감용
  // onMarkRead는 App.js에서 매 렌더링마다 새 함수가 생성되므로
  // ref로 캐시해서 useEffect dependency에서 제외
  const onMarkReadRef = useRef(onMarkRead);
  useEffect(() => { onMarkReadRef.current = onMarkRead; });

  const opponent = room.team1_name === myTeamName ? room.team2_name : room.team1_name;
  const sport = SPORTS[room.sports_type] || { icon: "🏅", label: room.sports_type || "-" };

  // 초기 데이터 로드 — room.id 기준으로 마운트 시 1회만 실행
  useEffect(() => {
    const roomId     = room.id;
    const myName     = myTeamName;
    const oppName    = opponent;

    const load = async () => {
      setLoading(true);
      const [msgRes, schedRes] = await Promise.all([
        supabase.from("messages").select("*").eq("room_id", roomId).order("created_at"),
        supabase.from("schedules").select("*").eq("room_id", roomId).maybeSingle(),
      ]);
      setMessages(msgRes.data || []);
      if (schedRes.data) setSchedule(schedRes.data);
      setLoading(false);

      // 입장 시 내가 받은 미읽음 메시지 전부 읽음 처리
      await supabase
        .from("messages")
        .update({ is_read: true })
        .eq("room_id", roomId)
        .neq("sender_name", myName)
        .eq("is_read", false);
      onMarkReadRef.current?.();

      // 상대팀 정보 조회 (알림용 ID + 모달 표시용 전체 데이터, 1회만)
      if (!opponentTeamIdRef.current) {
        const { data: t } = await supabase
          .from("teams").select("*").eq("team_name", oppName).maybeSingle();
        if (t) {
          opponentTeamIdRef.current = t.id;
          setOpponentTeam(t);
        }
      }

      // 내 팀 객체 조회 (MatchResult 전달용) — 1회만
      const { data: mt } = await supabase
        .from("teams").select("*").eq("team_name", myName).maybeSingle();
      if (mt) setMyTeam(mt);

      // 모집글 자동완성 + 교류전 확정 여부 + 호스트 여부 + 스코어 데이터
      if (room.match_id && !postDataRef.current) {
        const { data: app } = await supabase
          .from("applications")
          .select("id, post_id, host_team_id, guest_team_id, status")
          .eq("id", room.match_id)
          .maybeSingle();
        if (app) {
          setAppData(app);
          postIdRef.current = app.post_id || null;
          if (app.host_team_id) {
            const { data: ht } = await supabase
              .from("teams").select("team_name").eq("id", app.host_team_id).maybeSingle();
            if (ht) setIsHost(ht.team_name === myName);
          }
          if (app.post_id) {
            const { data: post } = await supabase
              .from("posts")
              .select("status, game_date, location_city, location_district, location_dong, location_name")
              .eq("id", app.post_id)
              .maybeSingle();
            if (post) {
              postDataRef.current = post;
              setIsMatchConfirmed(post.status === "confirmed");
            }
          }
        }
        setMatchStatusLoaded(true);

        // 기존 스코어 데이터 로드
        const { data: mr } = await supabase
          .from("match_results")
          .select("*")
          .eq("match_id", room.match_id)
          .maybeSingle();
        if (mr) setMatchResult(mr);

        // 내가 이미 매너 평가했는지 확인 (myTeam이 있을 때 확인)
        if (mt?.id) {
          const { data: rv } = await supabase
            .from("manner_reviews")
            .select("id")
            .eq("match_id", room.match_id)
            .eq("reviewer_team_id", mt.id)
            .maybeSingle();
          if (rv) setHasReviewed(true);
        }
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id]);

  // Realtime 구독 — room.id 기준으로 마운트 시 1회만 설정
  // myTeamName은 변하지 않으므로 초기값을 클로저에서 직접 사용해도 안전
  useEffect(() => {
    const myName = myTeamName;
    const channel = supabase
      .channel(`room-${room.id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "messages",
        filter: `room_id=eq.${room.id}`,
      }, (payload) => {
        setMessages((prev) => [...prev, payload.new]);
        // 현재 방에서 상대방 메시지를 받으면 즉시 읽음 처리
        if (payload.new.sender_name !== myName) {
          supabase.from("messages").update({ is_read: true }).eq("id", payload.new.id);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id]);

  // match_results Realtime 구독 — 채팅 채널과 완전히 분리
  useEffect(() => {
    if (!room.match_id) return;
    const ch = supabase
      .channel(`score_${room.match_id}`)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "match_results",
        filter: `match_id=eq.${room.match_id}`,
      }, (payload) => {
        if (payload.eventType === "DELETE") {
          setMatchResult(null);
        } else {
          setMatchResult(payload.new);
          setScoreRefreshKey((k) => k + 1);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.match_id]);

  // 새 메시지 왔을 때 스크롤
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const compressImage = (file) => new Promise((resolve) => {
    const img    = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        const maxSize = 1200;
        if (width > height && width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
        else if (height > maxSize)             { width  = (width  * maxSize) / height; height = maxSize; }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/webp", 0.8);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || imageUploading) return;
    e.target.value = "";
    setImageUploading(true);
    try {
      const compressed = await compressImage(file);
      if (!compressed) { alert("이미지 처리 중 오류가 발생했어요."); setImageUploading(false); return; }
      if (compressed.size > 5 * 1024 * 1024) { alert("이미지가 너무 커요 (최대 5MB)."); setImageUploading(false); return; }

      const fileName = `${room.id}/${Date.now()}.webp`;
      const { error: uploadErr } = await supabase.storage
        .from("chat-images").upload(fileName, compressed, { contentType: "image/webp" });
      if (uploadErr) { alert("업로드 중 오류가 발생했어요."); setImageUploading(false); return; }

      const { data: { publicUrl } } = supabase.storage.from("chat-images").getPublicUrl(fileName);

      const { error } = await supabase.from("messages").insert([{
        room_id:     room.id,
        sender_name: myTeamName,
        content:     "📷 사진",
        image_url:   publicUrl,
        is_read:     true,
      }]);
      if (!error) {
        await supabase.from("chat_rooms").update({
          last_message:    "📷 사진",
          last_message_at: new Date().toISOString(),
        }).eq("id", room.id);
        if (opponentTeamIdRef.current) {
          await supabase.from("notifications").insert([{
            team_id:    opponentTeamIdRef.current,
            type:       "chat_message",
            title:      `📷 ${myTeamName}의 새 사진`,
            body:       "사진을 보냈어요",
            related_id: room.id,
          }]);
        }
      }
    } catch (err) {
      alert("이미지 전송 중 오류가 발생했어요.");
      console.error(err);
    }
    setImageUploading(false);
  };

  const sendMessage = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    setInput("");
    const { error } = await supabase.from("messages").insert([{
      room_id:     room.id,
      sender_name: myTeamName,
      content,
      is_read:     true,  // 내가 보낸 메시지는 처음부터 읽음
    }]);
    if (!error) {
      await supabase.from("chat_rooms").update({
        last_message: content,
        last_message_at: new Date().toISOString(),
      }).eq("id", room.id);
      // 상대팀에게 채팅 알림 전송
      if (opponentTeamIdRef.current) {
        await supabase.from("notifications").insert([{
          team_id:    opponentTeamIdRef.current,
          type:       "chat_message",
          title:      `💬 ${myTeamName}의 새 메시지`,
          body:       content.length > 50 ? content.slice(0, 50) + "…" : content,
          related_id: room.id,
        }]);
      }
    }
    setSending(false);
  };

  const confirmMatch = async () => {
    if (!postIdRef.current) return;
    setConfirmLoading(true);
    // posts.status만 'confirmed'로 변경 (모집글 마감) — applications/chat_rooms 건드리지 않음
    await supabase.from("posts").update({ status: "confirmed" }).eq("id", postIdRef.current);
    if (opponentTeamIdRef.current) {
      await supabase.from("notifications").insert([{
        team_id:    opponentTeamIdRef.current,
        type:       "match_confirmed",
        title:      "✅ 교류전이 확정됐어요!",
        body:       `${myTeamName}과의 교류전이 확정됐어요 🎉`,
        related_id: room.id,
      }]);
    }
    setIsMatchConfirmed(true);
    setShowConfirmModal(false);
    setConfirmLoading(false);
  };

  const openScheduleModal = () => {
    const post = postDataRef.current;
    // 모집글에서 합친 장소 문자열 (기존 일정 없을 때만 자동완성)
    const locationFromPost = post
      ? [post.location_city, post.location_district, post.location_dong, post.location_name]
          .filter(Boolean).join(" ")
      : "";

    setSchedForm({
      game_date: schedule?.game_date || post?.game_date || "",
      game_time: schedule?.game_time || "",
      location:  schedule?.location  || locationFromPost,
      memo:      schedule?.memo      || "",
    });
    setShowSchedule(true);
  };

  const saveSchedule = async () => {
    if (!schedForm.game_date) return;
    setSchedLoading(true);
    let error;
    if (schedule?.id) {
      ({ error } = await supabase.from("schedules")
        .update({ ...schedForm, updated_at: new Date().toISOString() })
        .eq("id", schedule.id));
    } else {
      ({ error } = await supabase.from("schedules")
        .insert([{ room_id: room.id, ...schedForm }]));
    }
    if (!error) {
      const { data } = await supabase.from("schedules").select("*").eq("room_id", room.id).maybeSingle();
      if (data) setSchedule(data);
      setShowSchedule(false);
    }
    setSchedLoading(false);
  };

  // 채팅 나가기 — chat_rooms는 1:1 구조(team1_name/team2_name)에 참가자 목록이 없으므로,
  // 내 팀 이름을 left_teams 배열에 추가해 "나간 상태"로 표시한다. (상대팀에게는 계속 보임)
  // ⚠️ left_teams 컬럼이 없으면 update가 무시되며, 컬럼 추가 후 정상 동작한다.
  const handleLeaveChat = async () => {
    if (leaving) return;
    setLeaving(true);
    const current = Array.isArray(room.left_teams) ? room.left_teams : [];
    if (!current.includes(myTeamName)) {
      await supabase.from("chat_rooms")
        .update({ left_teams: [...current, myTeamName] })
        .eq("id", room.id);
    }
    setLeaving(false);
    setShowLeaveConfirm(false);
    onBack?.();
  };

  // 스코어 현황 상태 계산
  const myConfirmedScore  = isHost ? (matchResult?.host_confirmed  ?? false) : (matchResult?.guest_confirmed ?? false);
  const oppConfirmedScore = isHost ? (matchResult?.guest_confirmed ?? false) : (matchResult?.host_confirmed  ?? false);
  const scoreStatus = !room.match_id ? null
    : !matchResult             ? "none"
    : matchResult.result_status === "confirmed" ? "confirmed"
    : myConfirmedScore && !oppConfirmedScore    ? "waiting"
    : !myConfirmedScore && oppConfirmedScore    ? "confirm_needed"
    : "none";
  const needsBadge = scoreStatus === "confirm_needed";

  const hostTeamName  = isHost ? myTeamName : opponent;
  const guestTeamName = isHost ? opponent   : myTeamName;

  return (
    <div style={s.container}>
      {/* 헤더 */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={s.headerCenter} onClick={() => opponentTeam && setShowTeamModal(true)}>
          <div style={{ ...s.headerName, cursor: opponentTeam ? "pointer" : "default", textDecoration: opponentTeam ? "underline" : "none", textDecorationColor: "#d1d5db" }}>
            {opponent}
          </div>
          <div style={s.headerSub}>
            {sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: "1em", height: "1em", verticalAlign: "middle", objectFit: "contain" }} />} {sport.label}
            {opponentTeam && <span style={{ color: "#b0b8c1", marginLeft: 6, fontSize: 10 }}>👆 탭하면 팀 정보</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {room.match_id && (
            <button
              style={{ ...s.schedBtn, position: "relative" }}
              onClick={() => setShowScore(true)}
            >
              📊 스코어
              {needsBadge && <span style={s.scoreBadge} />}
            </button>
          )}
          {scoreStatus === "confirmed" && !hasReviewed && (
            <button style={s.mannerBtn} onClick={() => setShowManner(true)}>
              ⭐ 매너
            </button>
          )}
          <button style={s.schedBtn} onClick={openScheduleModal}>
            📅 일정
          </button>
          <button style={s.leaveBtn} onClick={() => setShowLeaveConfirm(true)}>
            나가기
          </button>
        </div>
      </div>

      {/* 일정 고정 배너 */}
      {schedule && (
        <div style={s.scheduleBanner} onClick={openScheduleModal}>
          <span style={{ fontSize: 16 }}>📅</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.bannerDate}>{formatDate(schedule.game_date)}</div>
            <div style={s.bannerSub}>
              {schedule.game_time && `${schedule.game_time} `}
              {schedule.location && `· ${schedule.location}`}
            </div>
          </div>
          <span style={s.bannerEdit}>수정 ›</span>
        </div>
      )}

      {/* 매칭 최종 승인 영역 */}
      {room.match_id && matchStatusLoaded && postIdRef.current && (
        isMatchConfirmed ? (
          <div style={s.confirmedBanner}>✅ 교류전 확정됨</div>
        ) : isHost ? (
          <button style={s.confirmBtn} onClick={() => setShowConfirmModal(true)}>
            ✅ 매칭 최종 승인
          </button>
        ) : (
          <div style={s.waitingBanner}>⏳ 상대팀 승인 대기 중</div>
        )
      )}

      {/* 스코어 상태 배너 */}
      {scoreStatus && (
        <button style={{
          ...s.scoreBanner,
          ...(scoreStatus === "confirm_needed" ? s.scoreBannerAlert : {}),
          ...(scoreStatus === "confirmed"      ? s.scoreBannerDone  : {}),
        }} onClick={() => setShowScore(true)}>
          {scoreStatus === "none"          && "📊 스코어 입력하기 ›"}
          {scoreStatus === "waiting"       && "⏳ 상대 팀의 스코어 확인 대기 중"}
          {scoreStatus === "confirm_needed" && "📋 스코어 확인 필요! 탭해서 확인하세요 →"}
          {scoreStatus === "confirmed"     && `✅ ${matchResult.host_score} : ${matchResult.guest_score} 스코어 확정됨`}
        </button>
      )}

      {/* 메시지 영역 */}
      <div style={s.messageArea}>
        {loading ? (
          <div style={s.centerWrap}>
            <div style={s.spinner} />
          </div>
        ) : messages.length === 0 ? (
          <div style={s.centerWrap}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>👋</div>
            <div style={{ fontSize: 14, color: "#9ca3af" }}>첫 메시지를 보내보세요!</div>
          </div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender_name === myTeamName;
            return (
              <div key={msg.id} style={{
                display: "flex",
                flexDirection: isMine ? "row-reverse" : "row",
                alignItems: "flex-end",
                gap: 6, marginBottom: 12,
              }}>
                {!isMine && (
                  <div style={s.avatar}>{msg.sender_name.charAt(0)}</div>
                )}
                <div>
                  {!isMine && (
                    <div style={s.senderName}>{msg.sender_name}</div>
                  )}
                  <div style={{
                    ...s.bubble,
                    background: isMine ? "#E24B4A" : "#fff",
                    color: isMine ? "#fff" : "#111827",
                    borderRadius: isMine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    border: isMine ? "none" : "1px solid #e5e7eb",
                    padding: msg.image_url ? 4 : undefined,
                    overflow: msg.image_url ? "hidden" : undefined,
                  }}>
                    {msg.image_url ? (
                      <img
                        src={msg.image_url}
                        alt="사진"
                        loading="lazy"
                        style={{ maxWidth: 200, display: "block", borderRadius: 10, cursor: "pointer" }}
                        onClick={() => setChatImageViewer(msg.image_url)}
                      />
                    ) : (
                      msg.content
                    )}
                  </div>
                  <div style={{ ...s.msgTime, textAlign: isMine ? "right" : "left" }}>
                    {new Date(msg.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* 안내 문구 */}
      <div style={s.notice}>
        💡 채팅방은 경기일 기준 5일 후 자동으로 사라집니다
      </div>

      {/* 이미지 전송 중 표시 */}
      {imageUploading && (
        <div style={{ textAlign: "center", fontSize: 12, color: "#E8412A", fontWeight: 600, padding: "4px 16px", flexShrink: 0 }}>
          📷 이미지 전송 중...
        </div>
      )}

      {/* 입력창 */}
      <div style={s.inputBar}>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleImageSelect}
        />
        <button
          style={{ ...s.photoBtn, opacity: imageUploading ? 0.4 : 1 }}
          onClick={() => fileInputRef.current?.click()}
          disabled={imageUploading}
        >
          📷
        </button>
        <input
          style={s.textInput}
          placeholder="메시지를 입력하세요..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
        />
        <button
          style={{ ...s.sendBtn, opacity: !input.trim() || sending ? 0.4 : 1 }}
          onClick={sendMessage}
          disabled={!input.trim() || sending}
        >
          전송
        </button>
      </div>

      {/* 상대팀 정보 모달 */}
      {showTeamModal && opponentTeam && (
        <OpponentTeamModal team={opponentTeam} onClose={() => setShowTeamModal(false)} />
      )}

      {/* 스코어 입력 / 확인 오버레이 */}
      {showScore && appData && myTeam && (
        <MatchResult
          key={scoreRefreshKey}
          app={appData}
          myTeam={myTeam}
          sportsType={room.sports_type}
          hostTeamName={hostTeamName}
          guestTeamName={guestTeamName}
          onClose={() => setShowScore(false)}
        />
      )}

      {/* 매너 평가 오버레이 */}
      {showManner && appData && myTeam && opponentTeamIdRef.current && (
        <MannerReview
          app={appData}
          myTeam={myTeam}
          opponentTeamId={opponentTeamIdRef.current}
          opponentTeamName={opponent}
          confirmedAt={matchResult?.confirmed_at}
          onClose={() => setShowManner(false)}
          onSubmitted={() => setHasReviewed(true)}
        />
      )}

      {/* 채팅 이미지 전체화면 뷰어 */}
      {chatImageViewer && (
        <ImageViewer src={chatImageViewer} onClose={() => setChatImageViewer(null)} />
      )}

      {/* 채팅 나가기 확인 모달 */}
      {showLeaveConfirm && (
        <div style={s.backdrop} onClick={() => !leaving && setShowLeaveConfirm(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🚪</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                채팅방을 나가시겠습니까?
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
                나가면 이 채팅방이 목록에서 사라집니다.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={s.cancelBtn} onClick={() => setShowLeaveConfirm(false)} disabled={leaving}>취소</button>
              <button
                style={{ ...s.primaryBtn, flex: 1, background: "#E24B4A", opacity: leaving ? 0.6 : 1 }}
                onClick={handleLeaveChat}
                disabled={leaving}
              >
                {leaving ? "나가는 중..." : "나가기"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 최종 승인 확인 모달 */}
      {showConfirmModal && (
        <div style={s.backdrop} onClick={() => setShowConfirmModal(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                매칭을 최종 승인하시겠어요?
              </div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                모집글이 마감됩니다.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={s.cancelBtn} onClick={() => setShowConfirmModal(false)} disabled={confirmLoading}>
                취소
              </button>
              <button
                style={{ ...s.primaryBtn, flex: 1, opacity: confirmLoading ? 0.6 : 1 }}
                onClick={confirmMatch}
                disabled={confirmLoading}
              >
                {confirmLoading ? "처리 중..." : "최종 승인"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 일정 입력 모달 */}
      {showSchedule && (
        <div style={s.backdrop} onClick={() => setShowSchedule(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalTitle}>📅 {schedule ? "일정 수정" : "일정 등록"}</div>
            <div style={s.modalSub}>
              등록된 일정은 채팅방 상단에 고정돼요
            </div>
            {!schedule && postDataRef.current && (
              <div style={{ marginBottom: 16, padding: "9px 12px", background: "#fff8f0", borderRadius: 8, border: "1px solid #fde68a", fontSize: 12, color: "#92400e" }}>
                📋 모집글 정보를 자동으로 불러왔어요. 수정해서 저장하세요.
              </div>
            )}

            <div style={s.formGroup}>
              <label style={s.formLabel}>경기 날짜 <span style={{ color: "#E24B4A" }}>*</span></label>
              <input type="date" style={s.formInput}
                value={schedForm.game_date}
                onChange={(e) => setSchedForm((p) => ({ ...p, game_date: e.target.value }))}
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>경기 시간</label>
              <input type="time" style={s.formInput}
                value={schedForm.game_time}
                onChange={(e) => setSchedForm((p) => ({ ...p, game_time: e.target.value }))}
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>장소</label>
              <input type="text" style={s.formInput}
                placeholder="예: 마포구민체육센터 3코트"
                value={schedForm.location}
                onChange={(e) => setSchedForm((p) => ({ ...p, location: e.target.value }))}
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel}>메모</label>
              <input type="text" style={s.formInput}
                placeholder="예: 주차 가능, 화장실 있음"
                value={schedForm.memo}
                onChange={(e) => setSchedForm((p) => ({ ...p, memo: e.target.value }))}
              />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button style={s.cancelBtn} onClick={() => setShowSchedule(false)}>취소</button>
              <button
                style={{ ...s.primaryBtn, flex: 1, opacity: schedLoading || !schedForm.game_date ? 0.6 : 1 }}
                onClick={saveSchedule}
                disabled={schedLoading || !schedForm.game_date}
              >
                {schedLoading ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function OpponentTeamModal({ team, onClose }) {
  const [viewerSrc, setViewerSrc] = useState(null);
  const sport        = SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type };
  const totalMembers = (team.male_count || 0) + (team.female_count || 0);

  return (
    <div style={sm.backdrop} onClick={onClose}>
      <div style={sm.box} onClick={(e) => e.stopPropagation()}>
        {/* 핸들 바 */}
        <div style={{ width: 36, height: 4, background: "#e5e7eb", borderRadius: 999, margin: "0 auto 16px" }} />

        {/* 히어로 */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          {team.logo_url ? (
            <img src={team.logo_url} alt="팀 로고" loading="lazy"
              style={{ width: 80, height: 80, borderRadius: 16, objectFit: "cover", border: "2px solid #e5e7eb", marginBottom: 10, cursor: "pointer" }}
              onClick={() => setViewerSrc(team.logo_url)}
            />
          ) : (
            <div style={{ fontSize: 48, marginBottom: 10 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 48, height: 48, objectFit: "contain" }} />}</div>
          )}
          <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{team.team_name}</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 10 }}>{sport.label}</div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap", marginBottom: 8 }}>
            {team.level && <SportLevelBadge level={team.level} />}
            {team.skill_level && (
              <span style={{ padding: "4px 12px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 12, fontWeight: 700, color: "#0369a1" }}>
                {team.skill_level}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <LevelBadge level={team.match_level} />
            <MannerBadge score={team.manner_avg} />
          </div>
          {team.region && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>📍 {team.region}</div>}
        </div>

        {/* 단체사진 */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>📸 팀 단체사진</div>
          {team.team_photo ? (
            <img src={team.team_photo} alt="단체사진" loading="lazy"
              style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 12, border: "1px solid #e5e7eb", cursor: "pointer", display: "block" }}
              onClick={() => setViewerSrc(team.team_photo)}
            />
          ) : (
            <div style={{ width: "100%", height: 100, background: "#f3f4f6", borderRadius: 12, border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#9ca3af" }}>
              📷 등록된 사진이 없습니다
            </div>
          )}
        </div>

        {/* 팀 정보 */}
        <div style={{ background: "#f5f6fa", borderRadius: 12, padding: "4px 14px", marginBottom: 14 }}>
          <OppInfoRow label="구력 레벨"      value={formatSportLevel(team.sports_type, team.level)} />
          <OppInfoRow label="실력"            value={team.skill_level || "미입력"} />
          <OppInfoRow label="전적 레벨"       value={formatRecordLevel(team)} />
          <OppInfoRow label="전적"            value={formatRecord(team)} />
          <OppInfoRow label="매너 점수"       value={formatManner(team)} />
          <OppInfoRow label="총 인원"         value={totalMembers > 0 ? `${totalMembers}명 (남 ${team.male_count || 0} / 여 ${team.female_count || 0})` : "미입력"} />
          <OppInfoRow label="연령대"          value={parseAgeGroup(team.age_group) || "미입력"} />
          <OppInfoRow label="평균 구력"       value={team.member_avg_career || "미입력"} />
          <OppInfoRow label="주요 활동 지역"  value={team.main_location || team.district || "미입력"} />
          <OppInfoRow label="주차"            value={team.parking ? `${team.parking}${team.parking_count ? ` (${team.parking_count}대)` : ""}` : "미입력"} />
        </div>

        {/* 팀 소개 */}
        <div style={{ background: "#f5f6fa", borderRadius: 12, padding: "14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>팀 소개</div>
          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>{team.intro || "아직 팀 소개글이 없어요"}</div>
        </div>

        <button style={sm.closeBtn} onClick={onClose}>닫기</button>
      </div>
      {viewerSrc && <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}
    </div>
  );
}

function OppInfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const sm = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, backdropFilter: "blur(4px)" },
  box:      { width: "90%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "20px 18px 24px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "85vh", overflowY: "auto" },
  closeBtn: { width: "100%", padding: "12px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer" },
};

const s = {
  container: { position: "fixed", top: 0, left: 0, right: 0, bottom: "calc(72px + env(safe-area-inset-bottom))", zIndex: 101, background: "#f5f6fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "calc(env(safe-area-inset-top) + 12px) 16px 12px", background: "#fff", borderBottom: "1px solid #e5e7eb", flexShrink: 0, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  backBtn: { background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", flexShrink: 0 },
  headerCenter: { flex: 1, minWidth: 0 },
  headerName: { fontSize: 16, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  headerSub: { fontSize: 12, color: "#9ca3af" },
  schedBtn: { background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer", padding: "6px 10px", flexShrink: 0 },
  leaveBtn: { background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#E24B4A", cursor: "pointer", padding: "6px 10px", flexShrink: 0 },
  scheduleBanner: { display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: "#fff8f0", borderBottom: "1px solid #fde68a", cursor: "pointer", flexShrink: 0 },
  bannerDate: { fontSize: 13, fontWeight: 700, color: "#92400e" },
  bannerSub: { fontSize: 11, color: "#b45309", marginTop: 1 },
  bannerEdit: { fontSize: 11, color: "#9ca3af", flexShrink: 0 },
  messageArea: { flex: 1, overflowY: "auto", padding: "16px 16px 8px", minHeight: 0 },
  centerWrap: { height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  spinner: { width: 28, height: 28, border: "3px solid #e5e7eb", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  avatar: { width: 32, height: 32, background: "#e5e7eb", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#6b7280", flexShrink: 0 },
  senderName: { fontSize: 11, color: "#9ca3af", marginBottom: 3, marginLeft: 2 },
  bubble: { maxWidth: 240, padding: "10px 14px", fontSize: 14, lineHeight: 1.5, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  msgTime: { fontSize: 10, color: "#c4c9d4", marginTop: 3 },
  confirmBtn:      { margin: "8px 16px 0", padding: "11px", background: "#111827", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", flexShrink: 0, textAlign: "center" },
  confirmedBanner: { margin: "8px 16px 0", padding: "10px 16px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#16a34a", textAlign: "center", flexShrink: 0 },
  waitingBanner:   { margin: "8px 16px 0", padding: "10px 16px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13, color: "#9ca3af", textAlign: "center", flexShrink: 0 },
  notice: { textAlign: "center", fontSize: 11, color: "#c4c9d4", padding: "6px 16px", flexShrink: 0 },
  inputBar: { display: "flex", gap: 8, padding: "10px 16px 16px", background: "#fff", borderTop: "1px solid #e5e7eb", flexShrink: 0 },
  photoBtn: { padding: "11px 12px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 18, cursor: "pointer", flexShrink: 0, lineHeight: 1 },
  textInput: { flex: 1, padding: "11px 14px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 10, color: "#111827", fontSize: 16, outline: "none" },
  sendBtn: { padding: "11px 18px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, backdropFilter: "blur(4px)" },
  modalBox: { width: "90%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "22px 20px 26px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "85vh", overflowY: "auto" },
  modalTitle: { fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 3 },
  modalSub: { fontSize: 12, color: "#9ca3af", marginBottom: 14 },
  formGroup: { marginBottom: 10 },
  formLabel: { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 },
  formInput: { width: "100%", padding: "9px 12px", background: "#f5f6fa", border: "1px solid #e5e7eb", borderRadius: 9, color: "#111827", fontSize: 13, outline: "none", boxSizing: "border-box" },
  primaryBtn: { padding: "11px 0", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: "pointer", width: "100%" },
  cancelBtn:       { padding: "11px 16px", background: "#f5f6fa", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  scoreBanner:     { display: "block", width: "100%", padding: "9px 16px", background: "#f0f9ff", borderBottom: "1px solid #bae6fd", fontSize: 12, color: "#0369a1", fontWeight: 600, textAlign: "center", cursor: "pointer", border: "none", flexShrink: 0 },
  scoreBannerAlert:{ background: "#fff5f5", borderBottomColor: "#fecaca", color: "#E24B4A" },
  scoreBannerDone: { background: "#f0fdf4", borderBottomColor: "#bbf7d0", color: "#16a34a" },
  scoreBadge:      { position: "absolute", top: 2, right: 2, width: 8, height: 8, background: "#E24B4A", borderRadius: "50%", display: "block" },
  mannerBtn:       { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#d97706", cursor: "pointer", padding: "6px 10px", flexShrink: 0 },
};
